import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize user data structure
  const initializeUserData = (email) => {
    const userData = {
      metadata: {
        joinedDate: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
      },
      files: {
        images: [],
        videos: [],
        documents: [],
      },
      passwords: [],
      notes: [],
    };
    localStorage.setItem(`userData_${email}`, JSON.stringify(userData));
    return userData;
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);

      // Update last login time
      const userData = JSON.parse(
        localStorage.getItem(`userData_${parsedUser.email}`) ||
          initializeUserData(parsedUser.email)
      );
      userData.metadata.lastLogin = new Date().toISOString();
      localStorage.setItem(
        `userData_${parsedUser.email}`,
        JSON.stringify(userData)
      );
    }
    setLoading(false);
  }, []);

  const signUp = (email, username, password) => {
    const users = JSON.parse(localStorage.getItem("users")) || [];
    const userExists = users.some(
      (u) => u.email === email || u.username === username
    );

    if (userExists) throw new Error("User already exists");

    const newUser = { email, username, password, id: uuidv4() };
    localStorage.setItem("users", JSON.stringify([...users, newUser]));

    // Initialize user data
    initializeUserData(email);

    setUser({ email, username });
    localStorage.setItem("user", JSON.stringify({ email, username }));
    navigate("/dashboard");
  };

  const signIn = (emailOrUsername, password) => {
    const users = JSON.parse(localStorage.getItem("users")) || [];
    const user = users.find(
      (u) =>
        (u.email === emailOrUsername || u.username === emailOrUsername) &&
        u.password === password
    );

    if (!user) throw new Error("Invalid credentials");

    // Update last login time
    const userData =
      JSON.parse(localStorage.getItem(`userData_${user.email}`)) ||
      initializeUserData(user.email);
    userData.metadata.lastLogin = new Date().toISOString();
    localStorage.setItem(`userData_${user.email}`, JSON.stringify(userData));

    setUser({ email: user.email, username: user.username });
    localStorage.setItem(
      "user",
      JSON.stringify({ email: user.email, username: user.username })
    );
    navigate("/dashboard");
  };

  const signOut = () => {
    setUser(null);
    localStorage.removeItem("user");
    navigate("/signin");
  };

  // User data management functions
  const getUserData = () => {
    if (!user) return null;
    return JSON.parse(localStorage.getItem(`userData_${user.email}`));
  };

  const updateUserData = (data) => {
    if (!user) return;
    localStorage.setItem(`userData_${user.email}`, JSON.stringify(data));
  };

  const addFile = async (fileType, file) => {
    if (!user) return;

    const userData =
      JSON.parse(localStorage.getItem(`userData_${user.email}`)) ||
      initializeUserData(user.email);

    const updatedFiles = [...userData.files[fileType], file];
    userData.files[fileType] = updatedFiles;

    localStorage.setItem(`userData_${user.email}`, JSON.stringify(userData));
    return updatedFiles;
  };

  const addPassword = (passwordData) => {
    if (!user) return;

    const userData =
      JSON.parse(localStorage.getItem(`userData_${user.email}`)) ||
      initializeUserData(user.email);

    const newPassword = {
      id: Date.now().toString(),
      ...passwordData,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
    };

    const updatedPasswords = [...userData.passwords, newPassword];
    userData.passwords = updatedPasswords;

    localStorage.setItem(`userData_${user.email}`, JSON.stringify(userData));
    return updatedPasswords;
  };

  const addNote = (noteData) => {
    if (!user) return;

    const userData =
      JSON.parse(localStorage.getItem(`userData_${user.email}`)) ||
      initializeUserData(user.email);

    const newNote = {
      id: Date.now().toString(),
      ...noteData,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
    };

    const updatedNotes = [...userData.notes, newNote];
    userData.notes = updatedNotes;

    localStorage.setItem(`userData_${user.email}`, JSON.stringify(userData));
    return updatedNotes;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signUp,
        signIn,
        signOut,
        getUserData,
        addFile,
        addPassword,
        addNote,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
